export const WEBFLOW_EMBED_CSS_WINDOW_KEY = "__VIABLE_WEBFLOW_EMBED_CSS__";

declare global {
	interface Window {
		__VIABLE_WEBFLOW_EMBED_CSS__?: string;
	}
}

/**
 * Contain the Webflow host so embed z-index (S4 z-50) cannot paint over sibling
 * navbar/footer, without `overflow` (that would break sticky S2/S3 stages).
 */
export const EMBED_HOST_CSS =
	":host{display:block;width:100%;position:relative;isolation:isolate;-webkit-text-stroke:none;}";

export function readCapturedEmbedCss(): string {
	if (typeof window === "undefined") {
		return "";
	}

	const value = window[WEBFLOW_EMBED_CSS_WINDOW_KEY];
	return typeof value === "string" ? value : "";
}

export function composeEmbedStyleText(baseCss: string, capturedCss = ""): string {
	return `${EMBED_HOST_CSS}\n${baseCss}\n${capturedCss}`;
}

const GOOGLE_MANROPE_STYLESHEET = "https://fonts.googleapis.com/css2?family=Manrope:wght@200..800&display=swap";

const EMBED_FONT_SANS_FALLBACK =
	'"Manrope", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

const EMBED_FONT_LINK_KEY = "viable-webflow-manrope";

const GENERIC_FONT_FAMILY =
	/^(ui-sans-serif|system-ui|-apple-system|blinkmacsystemfont|segoe ui|roboto|helvetica|arial|sans-serif)$/i;

/** Google Fonts @import inside a shadow <style> does not register @font-face globally. */
export function stripEmbedFontImports(cssText: string): string {
	return cssText.replace(/@import\s+url\(\s*["']https:\/\/fonts\.googleapis\.com[^"']+["']\s*\)\s*;?/g, "");
}

/**
 * Tailwind @theme sets `--font-sans: "Manrope", …` on :host. On Webflow the host
 * already serves Manrope under `"Manrope Variablefont Wght"`, so the hard-coded
 * family name misses and falls back to system UI. Strip it and sync from the host.
 */
export function stripThemeFontSans(cssText: string): string {
	return cssText.replace(/--font-sans\s*:\s*[^;]+;/g, "");
}

export function prepareEmbedStyleText(baseCss: string, capturedCss = ""): string {
	const composed = composeEmbedStyleText(baseCss, capturedCss);
	return stripThemeFontSans(stripEmbedFontImports(composed));
}

export function isGenericFontFamily(firstFamily: string): boolean {
	const normalized = firstFamily.trim().replace(/^["']|["']$/g, "");
	return GENERIC_FONT_FAMILY.test(normalized);
}

/** Loads Manrope on the host document when the page has no brand font yet. */
export function ensureEmbedFonts(): void {
	if (typeof document === "undefined") {
		return;
	}
	if (document.head.querySelector(`link[data-${EMBED_FONT_LINK_KEY}]`)) {
		return;
	}
	const link = document.createElement("link");
	link.rel = "stylesheet";
	link.href = GOOGLE_MANROPE_STYLESHEET;
	link.setAttribute(`data-${EMBED_FONT_LINK_KEY}`, "true");
	document.head.appendChild(link);
}

/**
 * Reuse the Webflow (or host) font stack inside the shadow tree. Custom properties
 * set inline on the host inherit into shadow descendants for `font-sans` utilities.
 */
export function syncEmbedFontFamily(host: HTMLElement): void {
	if (typeof window === "undefined") {
		return;
	}

	const firstFamily = getComputedStyle(host).fontFamily.split(",")[0] ?? "";
	if (!isGenericFontFamily(firstFamily)) {
		host.style.setProperty("--font-sans", getComputedStyle(host).fontFamily);
		return;
	}

	ensureEmbedFonts();
	host.style.setProperty("--font-sans", EMBED_FONT_SANS_FALLBACK);
}

const TW_PROPERTY_HOIST_KEY = "viable-webflow-tw-properties";

export function extractPropertyRegistrations(cssText: string): string[] {
	return cssText.match(/@property\s+[^{]+\{[^}]*\}/g) ?? [];
}

/**
 * Browsers ignore @property registrations inside shadow roots, so Tailwind v4
 * variable defaults (e.g. `--tw-translate-x`) stay unset there and every utility
 * composing them (`translate: var(--tw-translate-x) …`) computes to `none`.
 * Registrations are document-global, so hoisting them to `document.head` restores
 * the defaults inside the shadow tree. Only registrations are hoisted — style
 * rules still live exclusively in the shadow root.
 */
export function hoistTailwindPropertyRegistrations(cssText: string): void {
	if (typeof document === "undefined") {
		return;
	}
	if (document.head.querySelector(`style[data-${TW_PROPERTY_HOIST_KEY}]`)) {
		return;
	}
	const rules = extractPropertyRegistrations(cssText);
	if (rules.length === 0) {
		return;
	}
	const styleEl = document.createElement("style");
	styleEl.setAttribute(`data-${TW_PROPERTY_HOIST_KEY}`, "true");
	styleEl.textContent = rules.join("\n");
	document.head.appendChild(styleEl);
}

export function applyEmbedHostIsolation(host: HTMLElement): void {
	host.style.display = "block";
	host.style.width = "100%";
	host.style.position = "relative";
	host.style.isolation = "isolate";
}

export function measureEmbedContentHeight(root: HTMLElement): number {
	const rootBox = root.getBoundingClientRect();
	let bottom = rootBox.bottom;
	for (const node of root.querySelectorAll("*")) {
		const box = node.getBoundingClientRect();
		if (box.bottom > bottom) {
			bottom = box.bottom;
		}
	}
	return Math.max(0, Math.ceil(Math.max(rootBox.height, bottom - rootBox.top)));
}

export function syncEmbedHostHeight(host: HTMLElement, content: HTMLElement): () => void {
	const apply = () => {
		const height = measureEmbedContentHeight(content);
		if (height > 0) {
			host.style.minHeight = `${height}px`;
		}
	};

	apply();
	if (typeof ResizeObserver === "undefined") {
		return () => undefined;
	}

	const observer = new ResizeObserver(apply);
	observer.observe(content);
	return () => observer.disconnect();
}
